import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { BellIcon, CheckCircle2Icon } from '@/shared/ui/icons';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiNotificationReadPost,
  apiNotificationsReadAllPost,
  useNotificationsInfinite,
} from '@/entities/notifications';
import type { ApiNotification } from '@/shared/types/api/notifications';
import { WidgetsNotificationsItem } from './NotificationItem';
import { WidgetsNotificationsAnnouncementModal } from './AnnouncementModal';

export const WidgetsNotificationsMain = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const notifications = useNotificationsInfinite();
  const [announcement, setAnnouncement] = useState<ApiNotification | null>(null);

  const invalidateKeys = [queryKeys.notifications.all];

  const readOne = useMutate({
    api: apiNotificationReadPost,
    invalidateKeys,
  });

  const readAll = useMutate({
    api: apiNotificationsReadAllPost,
    invalidateKeys,
  });

  const handleSelect = (notification: ApiNotification): void => {
    if (!notification.is_read) readOne.mutate(notification.id);
    if (notification.type === 'CHAT') navigate('/support/chat');
    if (notification.type === 'ANNOUNCEMENT') setAnnouncement(notification);
  };

  const items = notifications.data?.pages.flatMap((p) => p.results) ?? [];
  const hasUnread = items.some((item) => !item.is_read);

  return (
    <>
      <PageHeader title={t('title')} showBack />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {hasUnread && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              loading={readAll.isPending}
              onClick={() => readAll.mutate()}
              leftIcon={<CheckCircle2Icon className="h-4 w-4" />}
            >
              {t('markAllRead')}
            </Button>
          </div>
        )}
        {notifications.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : notifications.isError ? (
          <ErrorState
            message={notifications.error.message}
            onRetry={() => void notifications.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<BellIcon className="h-10 w-10" />}
            title={t('emptyTitle')}
            description={t('emptyDescription')}
          />
        ) : (
          <>
            {items.map((notification) => (
              <WidgetsNotificationsItem
                key={notification.id}
                notification={notification}
                onSelect={handleSelect}
              />
            ))}
            {notifications.hasNextPage && (
              <Button
                variant="outline"
                onClick={() => void notifications.fetchNextPage()}
                loading={notifications.isFetchingNextPage}
                className="mt-2 self-center"
              >
                {t('loadMore')}
              </Button>
            )}
          </>
        )}
      </div>
      <WidgetsNotificationsAnnouncementModal
        notification={announcement}
        onClose={() => setAnnouncement(null)}
      />
    </>
  );
};
